import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw } from "lucide-react";
import { API, LEAD_STATUSES } from "../lib/site";

const fmtDate = (iso) =>
    new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem("nb_admin") || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [leads, setLeads] = useState(null);

    const load = useCallback(
        async (t) => {
            try {
                const { data } = await axios.get(`${API}/admin/leads`, {
                    headers: { Authorization: `Bearer ${t}` },
                });
                setLeads(data);
            } catch (e) {
                if (e.response?.status === 401) {
                    sessionStorage.removeItem("nb_admin");
                    setToken("");
                } else {
                    setError("Impossible de charger les demandes.");
                }
            }
        },
        [],
    );

    useEffect(() => {
        if (token) load(token);
    }, [token, load]);

    const login = async (ev) => {
        ev.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/admin/login`, { password });
            sessionStorage.setItem("nb_admin", data.token);
            setToken(data.token);
        } catch (e) {
            const detail = e.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Connexion impossible.");
        } finally {
            setLoading(false);
        }
    };

    const setStatus = async (id, status) => {
        setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
        try {
            await axios.patch(
                `${API}/admin/leads/${id}`,
                { status },
                { headers: { Authorization: `Bearer ${token}` } },
            );
        } catch {
            setError("La mise à jour du statut a échoué.");
            load(token);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("nb_admin");
        setToken("");
        setLeads(null);
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
                <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/10 bg-steel p-8">
                    <h1 className="font-display text-2xl font-extrabold text-white">Espace commercial</h1>
                    <p className="mt-1.5 text-sm text-zinc-400">Accès réservé à NEBOORZY.</p>
                    <input
                        type="password"
                        data-testid="admin-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Mot de passe"
                        className="mt-6 w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-white outline-none focus:border-signal"
                        autoComplete="current-password"
                    />
                    {error && (
                        <p data-testid="admin-login-error" className="mt-3 text-sm font-medium text-red-400">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        data-testid="admin-login-button"
                        disabled={loading}
                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-signal py-3 font-bold text-ink transition-colors hover:bg-signal-dark disabled:opacity-60"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        Se connecter
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-ink px-5 py-10 lg:px-10" data-testid="admin-dashboard">
            <div className="mx-auto max-w-6xl">
                <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-extrabold text-white">Demandes commerciales</h1>
                        <p className="mt-1 text-sm text-zinc-400">
                            {leads ? `${leads.length} demande${leads.length > 1 ? "s" : ""}` : "Chargement…"}
                        </p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            data-testid="admin-refresh-button"
                            onClick={() => load(token)}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-signal"
                        >
                            <RefreshCw className="h-4 w-4" /> Actualiser
                        </button>
                        <button
                            type="button"
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-signal"
                        >
                            <LogOut className="h-4 w-4" /> Déconnexion
                        </button>
                    </div>
                </div>
                {error && (
                    <p data-testid="admin-error" className="mb-5 rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-400">
                        {error}
                    </p>
                )}
                {!leads ? (
                    <div className="flex justify-center py-20 text-zinc-400">
                        <Loader2 className="h-8 w-8 animate-spin" />
                    </div>
                ) : leads.length === 0 ? (
                    <p data-testid="admin-empty" className="rounded-2xl border border-white/10 bg-steel p-10 text-center text-zinc-400">
                        Aucune demande pour le moment.
                    </p>
                ) : (
                    <ul className="space-y-4">
                        {leads.map((l) => (
                            <li key={l.id} data-testid={`admin-lead-${l.id}`} className="rounded-2xl border border-white/10 bg-steel p-6">
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                                            {fmtDate(l.created_at)} — {l.need_type} — {l.timing}
                                        </p>
                                        <h2 className="mt-1.5 font-display text-xl font-bold text-white">
                                            {l.name} · {l.company}
                                        </h2>
                                        <p className="mt-1 text-sm text-zinc-400">
                                            {l.phone} · {l.email} · {l.postal_code} {l.city}
                                        </p>
                                    </div>
                                    <select
                                        data-testid={`admin-status-${l.id}`}
                                        value={l.status}
                                        onChange={(e) => setStatus(l.id, e.target.value)}
                                        className="rounded-full border border-white/15 bg-ink px-4 py-2 text-sm font-semibold text-signal outline-none"
                                        aria-label="Statut de la demande"
                                    >
                                        {LEAD_STATUSES.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                        ))}
                                    </select>
                                </div>
                                {l.visit_requested && (
                                    <p className="mt-3 rounded-lg bg-signal/10 px-3 py-2 text-sm font-medium text-signal">
                                        Déplacement demandé : {l.visit_address || "adresse non précisée"}
                                    </p>
                                )}
                                {l.description && (
                                    <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">
                                        {l.description}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
